import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import useEventTracking from '@hooks/useEventTracking';
import useOnboardingPopper from '@hooks/useOnboardingPopper';
import useWindowSize from '@hooks/useWindowSize';
import Popper from '@mui/material/Popper';
import { api_mark_user_onboarding_tooltip_seen_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';
import request from '@utils/backend-api/request';

import SpotlightOverlay from './SpotlightOverlay';
import ArticleShare from './Steps/ArticleShare';
import { NAV_BREAKPOINT_PX, POPPER_Z_INDEX, STEP_COMPONENT_NAMES } from './constants';
import { getSteps, setActiveOnboardingStep } from './utils';

interface UserOnboardingTooltipProps {
  isProSubscriber: boolean;
}

const UserOnboardingTooltip = ({ isProSubscriber }: UserOnboardingTooltipProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [step, setStep] = useState(0);
  const { width } = useWindowSize();
  const isMobile = width < NAV_BREAKPOINT_PX;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isArticlePage = pathname.startsWith('/articles/') && pathname.length > '/articles/'.length;
  const steps = useMemo(
    () => getSteps(isProSubscriber, isMobile, isArticlePage),
    [isProSubscriber, isMobile, isArticlePage]
  );
  const totalSteps = steps.length;

  const { trackEvent } = useEventTracking({
    location: TrackingLocation.UserOnboardingTooltip,
  });

  const { popperConfig, popperAnchor, spotlightEls, useCenterAnchor, setCenterAnchorEl } =
    useOnboardingPopper(step, steps, isMobile);

  const markTooltipSeen = useCallback(() => {
    request('post', api_mark_user_onboarding_tooltip_seen_path()).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isOpen) {
      trackEvent(
        impression({
          impressionElement: ImpressionElement.UserOnboardingTooltip,
          context: STEP_COMPONENT_NAMES.get(steps[step]) ?? `step_${step}`,
        })
      );
    }
  }, [step, isOpen]);

  const currentStepComponent = steps[step];
  const isArticleShareStep = currentStepComponent === ArticleShare;
  useEffect(() => {
    if (!isMobile && isArticleShareStep && popperAnchor) {
      popperAnchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isMobile, isArticleShareStep, popperAnchor]);

  const handleNext = () => {
    if (step < totalSteps - 1) {
      const stepName = STEP_COMPONENT_NAMES.get(steps[step]) ?? `step_${step}`;
      if (step === 0) {
        trackEvent(click({ clickTarget: ClickTarget.OnboardingTooltipStart, clickText: stepName }));
      } else {
        trackEvent(click({ clickTarget: ClickTarget.OnboardingTooltipNext, clickText: stepName }));
      }
      setStep((prev) => prev + 1);
    } else {
      trackEvent(click({ clickTarget: ClickTarget.OnboardingComplete }));
      markTooltipSeen();
      setIsOpen(false);
      setActiveOnboardingStep(null);
    }
  };

  const handleClose = () => {
    const stepName = STEP_COMPONENT_NAMES.get(steps[step]) ?? `step_${step}`;
    trackEvent(click({ clickTarget: ClickTarget.OnboardingTooltipSkip, clickText: stepName }));
    markTooltipSeen();
    setIsOpen(false);
    setActiveOnboardingStep(null);
  };

  const stepProps = {
    step,
    totalSteps: isArticlePage ? 6 : 5,
    isProSubscriber,
    handleNext,
    handleClose,
  };

  const renderStep = () => {
    const StepComponent = steps[step];
    if (StepComponent) {
      return <StepComponent {...stepProps} />;
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <>
      {createPortal(
        <>
          <SpotlightOverlay anchorEls={isMobile ? [] : spotlightEls} />
          {useCenterAnchor && !isMobile && (
            <div
              ref={setCenterAnchorEl}
              style={{
                position: 'fixed',
                left: '50%',
                top: '40%',
                transform: 'translate(-50%, -50%)',
                width: 0,
                height: 0,
              }}
              aria-hidden="true"
            />
          )}
          {isMobile ? (
            <div
              className="fixed inset-0 flex items-center justify-center overflow-y-auto px-16 py-16"
              style={{ zIndex: POPPER_Z_INDEX }}
              aria-hidden="false"
            >
              <div className="max-h-[90vh] shadow-[0_0_15px_rgba(0,0,0,0.1)]">{renderStep()}</div>
            </div>
          ) : (
            popperConfig &&
            popperAnchor && (
              <Popper
                open={isOpen}
                anchorEl={popperAnchor}
                placement={popperConfig.placement ?? 'bottom-start'}
                style={{ zIndex: POPPER_Z_INDEX }}
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: popperConfig.offset ?? [0, 4],
                    },
                  },
                  {
                    name: 'flip',
                    enabled: true,
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'clippingParents',
                      padding: 16,
                      altAxis: true,
                      tether: false,
                    },
                  },
                ]}
              >
                <div className="shadow-[0_0_15px_rgba(0,0,0,0.1)]">{renderStep()}</div>
              </Popper>
            )
          )}
        </>,
        document.body
      )}
    </>
  );
};

export default UserOnboardingTooltip;
